import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api';
import { dayLabel } from '../dates';
import { length, money } from './format';
import { act, usePortalLoad } from './offline';
import { Actions, LoadFailure, SavedNote } from './shared';

interface Offer {
  assignment_id: number;
  suburb: string;
  date: string;
  start_time: string;
  minutes: number;
  pay_rate: string | null;
  pay_rate_unit: string | null;
  /** How the job repeats, until when, and its next visit dates; absent from older saved copies. */
  repeats?: string | null;
  until?: string | null;
  next_dates?: string[];
}

const REPEATS: Record<string, string> = {
  weekly: 'Every week',
  fortnightly: 'Every 2 weeks',
  triweekly: 'Every 3 weeks',
  monthly: 'Every month',
  manual: 'On set dates',
};

/** Taking a job means every visit: "Every 2 weeks · until Thu, 31 Dec". */
const howOften = (o: Offer) =>
  [o.repeats ? REPEATS[o.repeats] : '', o.until ? `until ${dayLabel(o.until)}` : '']
    .filter(Boolean)
    .join(' · ');

export function PortalOffers() {
  const load = usePortalLoad<{ items: Offer[] }>('/api/portal/offers');
  const { data } = load;
  const [choice, setChoice] = useState<Offer | null>(null);
  const [states, setStates] = useState<Record<number, 'claiming' | 'mine' | 'taken'>>({});
  const [message, setMessage] = useState<{ text: string; took?: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  if (!data) {
    return (
      <>
        <h1>Available jobs</h1>
        {load.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <LoadFailure what="the jobs on offer" load={load} />
        )}
      </>
    );
  }

  async function claim(o: Offer) {
    setBusy(true);
    setMessage(null);
    try {
      const r = await act(
        `Take the ${o.suburb} job`,
        'POST',
        `/api/portal/offers/${o.assignment_id}/claim`,
      );
      setStates({ ...states, [o.assignment_id]: r === 'queued' ? 'claiming' : 'mine' });
      setMessage(
        r === 'queued'
          ? {
              text: 'Saved on this phone. It will be sent when you have a signal, and someone else may take it first.',
            }
          : { text: `The ${o.suburb} job is yours, starting ${dayLabel(o.date)}.`, took: true },
      );
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setStates({ ...states, [o.assignment_id]: 'taken' });
      }
      setMessage({ text: e instanceof ApiError ? e.message : 'Something went wrong.' });
    }
    setBusy(false);
    setChoice(null);
  }

  return (
    <>
      <h1>Available jobs</h1>
      <p className="muted p-sub">Jobs the office has put up. The first to take one gets it.</p>
      <SavedNote load={load} extra="Some may have been taken since." />
      {message && (
        <p role="status" className={message.took ? 'p-done' : 'notice'}>
          {message.took && <strong>Added to your schedule. </strong>}
          {message.text} {message.took && <Link to="/week">See your week</Link>}
        </p>
      )}
      {data.items.length === 0 && <p className="empty">Nothing is on offer right now.</p>}
      <ul className="p-list">
        {data.items.map((o) => (
          <li key={o.assignment_id} className="p-visit">
            <strong>
              {dayLabel(o.date)}, {o.start_time}
            </strong>
            {howOften(o) && <span>{howOften(o)}</span>}
            {o.next_dates && o.next_dates.length > 1 && (
              <span className="muted">Next visits: {o.next_dates.map(dayLabel).join(' · ')}</span>
            )}
            <span>
              {o.suburb} · {length(o.minutes)} per visit
            </span>
            {o.pay_rate ? (
              <span className="p-pay">
                {money(o.pay_rate)} per {o.pay_rate_unit}
              </span>
            ) : (
              <span className="muted">Pay: ask the office</span>
            )}
            {states[o.assignment_id] === 'claiming' ? (
              <span className="p-state">Claiming… (waiting to send)</span>
            ) : states[o.assignment_id] === 'mine' ? (
              <span className="p-state mine">You&apos;re on this job</span>
            ) : states[o.assignment_id] === 'taken' ? (
              <span className="p-state gone">Someone else got this</span>
            ) : (
              <Actions>
                <button type="button" className="quiet p-big" onClick={() => setChoice(o)}>
                  Take this job
                  <span className="sr-only">
                    {' '}
                    in {o.suburb}, from {dayLabel(o.date)}
                  </span>
                </button>
              </Actions>
            )}
          </li>
        ))}
      </ul>
      {choice && (
        <ConfirmSheet
          offer={choice}
          busy={busy}
          onConfirm={() => void claim(choice)}
          onCancel={() => setChoice(null)}
        />
      )}
    </>
  );
}

/**
 * The confirm sheet: says what taking the work means, and asks once more. Focus starts on
 * the question, not on "Yes", so a stray Enter commits nothing; Tab stays inside, and Escape
 * or a tap outside closes it. Focus goes back to where it was.
 */
function ConfirmSheet(props: { offer: Offer; busy: boolean; onConfirm(): void; onCancel(): void }) {
  const sheet = useRef<HTMLDivElement>(null);
  const title = useRef<HTMLHeadingElement>(null);
  const cancel = useRef(props.onCancel);
  cancel.current = props.onCancel;
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    title.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancel.current();
      if (e.key !== 'Tab' || !sheet.current) return;
      const items = [...sheet.current.querySelectorAll<HTMLElement>('button:not(:disabled)')];
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      const at = document.activeElement;
      if (e.shiftKey && (at === first || at === title.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && at === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      before?.focus?.();
    };
  }, []);
  const o = props.offer;
  return (
    <div
      className="p-sheet-back"
      onClick={(e) => {
        if (e.target === e.currentTarget) props.onCancel();
      }}
    >
      <div
        ref={sheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sheet-h"
        className="p-sheet"
      >
        <h2 id="sheet-h" ref={title} tabIndex={-1}>
          Take this job?
        </h2>
        <p>
          {o.suburb}, starting {dayLabel(o.date)} at {o.start_time}, {length(o.minutes)} per visit.
        </p>
        {howOften(o) && (
          <p>
            <strong>{howOften(o)}</strong>
          </p>
        )}
        <p>
          You will be the lead contractor for every visit of this job, not just the first. If you
          need to give it up later, you will have to ask the office.
        </p>
        <div className="row">
          <button type="button" className="p-big" onClick={props.onConfirm} disabled={props.busy}>
            Yes, I will take it
          </button>
          <button type="button" className="quiet p-big" onClick={props.onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
